// types
import type {
  AutocompletePrediction,
  Options,
  Suggestion,
} from './autocomplete.types';

export function buildOptions({
  coordinates,
  country,
  googleMapsRef,
  mode,
  radius,
  userInput,
}: Options) {
  const options: google.maps.places.AutocompletionRequest = {
    input: userInput,
    location: new googleMapsRef!.LatLng(coordinates.lat, coordinates.lng),
    radius,
    componentRestrictions: { country: '' },
  };

  // if we have a country prop set, add the componentRestrictions property
  // to the options object
  if (mode !== 'hotel' && country && country !== 'GISA') {
    if (country === 'CH') {
      options.componentRestrictions = {
        country: ['CH', 'LI'],
      };
    } else {
      options.componentRestrictions = {
        country,
      };
    }
  }

  if (options.componentRestrictions?.country && options.componentRestrictions.country.length > 5) {
    options.componentRestrictions.country = null;
  }
  return options;
}

enum StatusMessage {
  OK = 'The response contains a valid result.',
  ZERO_RESULTS = '',
  INVALID_REQUEST = 'This request was invalid.',
  OVER_QUERY_LIMIT = 'The application has gone over its request quota.',
  REQUEST_DENIED = 'The application is not allowed to use the PlacesService.',
  UNKNOWN_ERROR = `The PlacesService request could not be processed \n
                due to a server error.The request may succeed if you try again.`,
}

export function buildPromiseCreator(
  autocompleteService: google.maps.places.AutocompleteService,
  options: google.maps.places.AutocompletionRequest,
) {
  return (type: string) =>
    new Promise<{ payload: AutocompletePrediction[]; message: string } | Record<string, never>>(
      (resolve, reject) => {
        autocompleteService.getPlacePredictions(
          { types: [type], ...options },
          (
            suggestions: AutocompletePrediction[],
            status: google.maps.places.PlacesServiceStatus,
          ) => {
            if (status === 'OK') {
              resolve({ payload: suggestions, message: StatusMessage.OK });
            }

            if (status === 'ZERO_RESULTS') {
              resolve({});
            }

            reject(StatusMessage[status]);
          },
        );
      },
    );
}
/**
 * Normalize the suggestions and persist them in local state.
 * This triggers a re-render and displays all suggestions inside
 * the <SuggestList> component.
 * @param {Array} updatedSuggests - An array containing all google autocomplete suggestions.
 */
export const normalizeSuggests = (
  updatedSuggests?: AutocompletePrediction[],
): Suggestion[] | null => {
  // If we have suggestions, normalize them...
  if (updatedSuggests) {
    const normalizedSuggests = updatedSuggests.map((suggest: AutocompletePrediction) => ({
      mainText: suggest.structured_formatting.main_text,
      secondaryText: suggest.structured_formatting.secondary_text,
      matchedSubstrings: suggest.structured_formatting.main_text_matched_substrings[0],
      description: suggest.description,
      placeId: suggest.place_id,
    }));

    return normalizedSuggests;
  }

  return null;
};
